/* ==========================================================================
   Moonbow Skin — Cart drawer behaviour
   Markup: snippets/moonbow-cart-drawer*.liquid, moonbow-cart-qty.liquid.

   Everything is delegated from `document`, because the theme's CartItems /
   CartFooter components morph the drawer's HTML after every change — nothing
   here holds a reference to an element that can be replaced. Listeners use
   the capture phase: the theme's drawer panel has `@click.stop`, so a click
   never bubbles up to `document`.

   Each action posts to Shopify's Ajax cart with the four cart sections and
   then fires the theme's own events, so the drawer, header count, live
   region and gwp.js all update through the existing pipeline:
     cart change / update -> `shapes:cart:cartqtychange`
     cart add             -> `shapes:cart:afteradditem`
   ========================================================================== */
(function () {
  'use strict';

  var SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';
  var busy = false;

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function drawer() {
    return document.querySelector('.mb-cart');
  }

  function setBusy(state) {
    busy = state;
    var el = drawer();
    if (el) el.classList.toggle('is-busy', state);
  }

  /* Shopify's own messages ("only 3 left") are shown as they are; a request
     that never reached Shopify gets the theme's plain-language string
     instead of the browser's "Failed to fetch". */
  function showError(message) {
    var el = document.getElementById('cart-errors');
    if (!el) return;
    if (message === 'Failed to fetch' || /NetworkError|Load failed/i.test(message || '')) {
      message =
        (window.theme && window.theme.strings && window.theme.strings.cartError) ||
        'Something went wrong. Please try again.';
    }
    el.textContent = message || '';
  }

  function post(url, body) {
    body.sections = SECTIONS;
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          throw new Error(
            data.description ||
              data.message ||
              (window.theme && window.theme.strings && window.theme.strings.cartError) ||
              'Something went wrong. Please try again.'
          );
        }
        return data;
      });
    });
  }

  function emit(name, data, extra) {
    var detail = { response: data };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        detail[k] = extra[k];
      });
    }
    document.body.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /* One request at a time; the drawer dims while it runs. */
  function run(url, body, eventName, extra) {
    if (busy) return Promise.resolve();
    setBusy(true);
    showError('');
    return post(url, body)
      .then(function (data) {
        emit(eventName || 'shapes:cart:cartqtychange', data, extra);
      })
      .catch(function (error) {
        showError(error.message);
      })
      .then(function () {
        setBusy(false);
      });
  }

  function change(key, quantity, extra) {
    var body = { id: key, quantity: quantity };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        body[k] = extra[k];
      });
    }
    return run(routes().cart_change_url ? routes().cart_change_url + '.js' : '/cart/change.js', body, null, {
      key: key,
      quantity: quantity
    });
  }

  function update(body) {
    return run(routes().cart_update_url ? routes().cart_update_url + '.js' : '/cart/update.js', body);
  }

  /* "key:units,key:units" -> { key: units * sets } */
  function bundleUpdates(spec, sets) {
    var updates = {};
    spec.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      if (parts[0]) updates[parts[0]] = (Number(parts[1]) || 1) * sets;
    });
    return updates;
  }

  function codesOf(form) {
    return (form.getAttribute('data-codes') || '')
      .split(',')
      .map(function (c) {
        return c.trim();
      })
      .filter(Boolean);
  }

  /* --------------------------------------------------------------- clicks */

  document.addEventListener('click', function (event) {
    var el = event.target.closest(
      '.mb-cart [data-mb-cart-qty], .mb-cart [data-mb-cart-bundle], .mb-cart [data-mb-cart-plan-set], ' +
        '.mb-cart [data-mb-cart-add], .mb-cart [data-mb-cart-discount-remove], .mb-cart [data-mb-cart-scroll]'
    );
    if (!el) return;
    event.preventDefault();

    /* Quantity +/- and Remove on a single line. */
    if (el.hasAttribute('data-mb-cart-qty')) {
      change(el.getAttribute('data-key'), Math.max(0, Number(el.getAttribute('data-qty')) || 0));
      return;
    }

    /* Quantity +/- and Remove on a bundle: every line scales together. */
    if (el.hasAttribute('data-mb-cart-bundle')) {
      var sets = Math.max(0, Number(el.getAttribute('data-qty')) || 0);
      update({ updates: bundleUpdates(el.getAttribute('data-mb-cart-bundle'), sets) });
      return;
    }

    /* "Upgrade to Subscription & Save x%". */
    if (el.hasAttribute('data-mb-cart-plan-set')) {
      change(el.getAttribute('data-key'), Number(el.getAttribute('data-qty')) || 1, {
        selling_plan: Number(el.getAttribute('data-mb-cart-plan-set'))
      });
      return;
    }

    /* Carousel "Add to cart", with the plan picked on the card (if any). */
    if (el.hasAttribute('data-mb-cart-add')) {
      if (busy) return;
      var card = el.closest('.mb-cart__card');
      var planSelect = card && card.querySelector('[data-mb-cart-add-plan]');
      var item = { id: Number(el.getAttribute('data-mb-cart-add')), quantity: 1 };
      if (planSelect && planSelect.value) item.selling_plan = Number(planSelect.value);
      el.classList.add('is-loading');
      run(routes().cart_add_url ? routes().cart_add_url + '.js' : '/cart/add.js', { items: [item] }, 'shapes:cart:afteradditem').then(
        function () {
          el.classList.remove('is-loading');
        }
      );
      return;
    }

    /* Remove one discount code; the others stay applied. */
    if (el.hasAttribute('data-mb-cart-discount-remove')) {
      var form = document.querySelector('.mb-cart [data-mb-cart-discount]');
      var drop = el.getAttribute('data-mb-cart-discount-remove').toLowerCase();
      var keep = form
        ? codesOf(form).filter(function (c) {
            return c.toLowerCase() !== drop;
          })
        : [];
      update({ discount: keep.join(',') });
      return;
    }

    /* Carousel arrows: one card per click. */
    if (el.hasAttribute('data-mb-cart-scroll')) {
      var carousel = el.closest('[data-mb-cart-carousel]');
      var track = carousel && carousel.querySelector('.mb-cart__track');
      var first = track && track.firstElementChild;
      if (!track || !first) return;
      var step = first.getBoundingClientRect().width + 14;
      track.scrollBy({ left: step * Number(el.getAttribute('data-mb-cart-scroll')), behavior: 'smooth' });
    }
  }, true);

  /* ------------------------------------------------- plan selects (change) */

  document.addEventListener('change', function (event) {
    var select = event.target.closest('.mb-cart [data-mb-cart-plan]');
    if (!select) return;
    var plan = select.value ? Number(select.value) : null;
    change(select.getAttribute('data-key'), Number(select.getAttribute('data-qty')) || 1, {
      selling_plan: plan
    });
  }, true);

  /* ------------------------------------------------------ discount submit */

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.mb-cart [data-mb-cart-discount]');
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector('input');
    var code = input ? input.value.trim() : '';
    if (!code) {
      if (input) input.focus();
      return;
    }
    var codes = codesOf(form);
    if (
      codes.some(function (c) {
        return c.toLowerCase() === code.toLowerCase();
      })
    ) {
      input.value = '';
      return;
    }
    if (busy) return;

    /* Liquid's `cart.discount_codes` only lists codes that apply, so a code
       that doesn't would vanish silently and stay on the cart. Check the
       cart JSON instead: if it didn't apply, take it off again and say so. */
    var url = routes().cart_update_url ? routes().cart_update_url + '.js' : '/cart/update.js';
    var message = '';
    setBusy(true);
    showError('');
    post(url, { discount: codes.concat(code).join(',') })
      .then(function (data) {
        var entry = (data.discount_codes || []).filter(function (c) {
          return c.code.toLowerCase() === code.toLowerCase();
        })[0];
        if (entry && entry.applicable) return data;
        message = '“' + code + '” can’t be applied to this cart.';
        return post(url, { discount: codes.join(',') });
      })
      .then(function (data) {
        emit('shapes:cart:cartqtychange', data);
        var note = document.querySelector('.mb-cart [data-mb-cart-discount-msg]');
        if (note) note.textContent = message;
      })
      .catch(function (error) {
        showError(error.message);
      })
      .then(function () {
        setBusy(false);
      });
  }, true);

  /* ------------------------------------------ carousel arrow enabled state
     Prev starts disabled in the markup; scrolling keeps both arrows honest. */

  function syncArrows(track) {
    var carousel = track.closest('[data-mb-cart-carousel]');
    if (!carousel) return;
    var prev = carousel.querySelector('[data-mb-cart-scroll="-1"]');
    var next = carousel.querySelector('[data-mb-cart-scroll="1"]');
    var max = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (track && track.classList && track.classList.contains('mb-cart__track')) syncArrows(track);
    },
    true
  );

  /*
    The `cart-items` / `cart-footer` sections print the old theme's cart page
    when they are rendered in the /cart page's context. A cart refresh that
    asks for them that way (some apps fetch `/cart?sections=...`) would morph
    the cart page into this drawer. Off the cart page, such an update is
    stopped before the theme's components see it and re-sent with the
    sections rendered for this page - the drawer's own markup.
  */
  if (!/^\/cart\/?$/.test(window.location.pathname)) {
    ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach(function (name) {
      window.addEventListener(
        name,
        function (event) {
          var response = event.detail && event.detail.response;
          var html = response && response.sections && response.sections['cart-items'];
          if (!html || html.indexOf('mb-cart__body') !== -1) return;
          event.stopImmediatePropagation();
          var target = event.target;
          fetch(window.location.pathname + '?sections=' + SECTIONS)
            .then(function (r) {
              return r.json();
            })
            .then(function (sections) {
              var detail = Object.assign({}, event.detail, {
                response: Object.assign({}, response, { sections: sections })
              });
              target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
            })
            .catch(function () {});
        },
        true
      );
    });
  }

})();

/* ==========================================================================
   2026-10-21 Frequency dropdown (Iram): the line's "Deliver Every …" control
   opens a styled list instead of the browser's native select menu.

   The native <select data-mb-cart-plan> stays in the markup and keeps doing
   the work: choosing an option sets its value and fires `change`, so the
   plan-select handler above posts the new selling plan exactly as before.
   The drawer is morphed after every cart change, so the enhancement re-runs
   on any mutation inside the drawer (selects already done are skipped).
   Listeners use the capture phase, because the drawer panel stops clicks.
   ========================================================================== */
(function () {
  'use strict';

  var OPEN = 'is-open';
  var current = null;

  function closeCurrent(focusTrigger) {
    if (!current) return;
    var c = current;
    current = null;
    c.panel.remove();
    c.label.classList.remove(OPEN);
    c.button.setAttribute('aria-expanded', 'false');
    if (focusTrigger) c.button.focus();
  }

  function optionsOf(select) {
    var items = [];
    Array.prototype.forEach.call(select.children, function (child) {
      if (child.tagName === 'OPTGROUP') {
        items.push({ group: child.label });
        Array.prototype.forEach.call(child.children, function (opt) {
          items.push({ value: opt.value, text: opt.textContent.trim() });
        });
      } else if (child.tagName === 'OPTION') {
        items.push({ value: child.value, text: child.textContent.trim() });
      }
    });
    return items;
  }

  function open(entry) {
    closeCurrent(false);
    var cart = entry.label.closest('.mb-cart');
    if (!cart) return;

    var panel = document.createElement('div');
    panel.className = 'mb-cart__plan-panel';
    panel.setAttribute('role', 'listbox');
    panel.id = entry.button.getAttribute('aria-controls');

    optionsOf(entry.select).forEach(function (item) {
      if (item.group !== undefined) {
        var head = document.createElement('p');
        head.className = 'mb-cart__plan-group';
        head.textContent = item.group;
        panel.appendChild(head);
        return;
      }
      var opt = document.createElement('div');
      opt.className = 'mb-cart__plan-option';
      opt.setAttribute('role', 'option');
      opt.tabIndex = -1;
      opt.setAttribute('data-value', item.value);
      opt.setAttribute('aria-selected', item.value === entry.select.value ? 'true' : 'false');
      opt.textContent = item.text;
      panel.appendChild(opt);
    });

    cart.appendChild(panel);

    /* Placed against the drawer, so the line list's scroll box cannot clip
       it: under the trigger, or above it when there is no room below. */
    var c = cart.getBoundingClientRect();
    var b = entry.button.getBoundingClientRect();
    var left = Math.max(8, Math.min(b.left - c.left - 10, c.width - panel.offsetWidth - 8));
    var below = b.bottom - c.top + 6;
    var top = below + panel.offsetHeight > c.height - 8 ? b.top - c.top - panel.offsetHeight - 6 : below;
    panel.style.left = left + 'px';
    panel.style.top = Math.max(8, top) + 'px';

    entry.label.classList.add(OPEN);
    entry.button.setAttribute('aria-expanded', 'true');
    entry.panel = panel;
    current = entry;

    var selected = panel.querySelector('[aria-selected="true"]') || panel.querySelector('.mb-cart__plan-option');
    if (selected) selected.focus();
  }

  function choose(entry, value) {
    closeCurrent(true);
    if (value === entry.select.value) return;
    entry.select.value = value;
    entry.text.textContent = entry.select.options[entry.select.selectedIndex].textContent.trim();
    entry.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  var seq = 0;

  function enhance() {
    document.querySelectorAll('.mb-cart [data-mb-cart-plan]').forEach(function (select) {
      var label = select.closest('.mb-cart__select');
      if (!label || label.querySelector('.mb-cart__plan-trigger')) return;

      label.classList.add('mb-cart__select--custom');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'mb-cart__plan-trigger';
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', 'mb-cart-plan-' + ++seq);
      button.setAttribute('aria-label', 'Delivery frequency');

      var text = document.createElement('span');
      text.textContent = select.options[select.selectedIndex] ? select.options[select.selectedIndex].textContent.trim() : '';
      button.appendChild(text);

      var icon = label.querySelector('.mb-icon');
      if (icon) button.appendChild(icon.cloneNode(true));

      label.insertBefore(button, label.firstChild);
      label._mbPlan = { label: label, select: select, button: button, text: text };
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var option = current && event.target.closest('.mb-cart__plan-option');
      if (option && current.panel.contains(option)) {
        event.preventDefault();
        choose(current, option.getAttribute('data-value'));
        return;
      }

      var trigger = event.target.closest('.mb-cart__plan-trigger');
      if (trigger) {
        event.preventDefault();
        var entry = trigger.closest('.mb-cart__select')._mbPlan;
        if (!entry) return;
        if (current && current.button === trigger) closeCurrent(false);
        else open(entry);
        return;
      }

      if (current && !event.target.closest('.mb-cart__plan-panel')) closeCurrent(false);
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (event) {
      if (!current) return;
      var opts = Array.prototype.slice.call(current.panel.querySelectorAll('.mb-cart__plan-option'));
      var i = opts.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (opts[i + 1] || opts[0]).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (opts[i - 1] || opts[opts.length - 1]).focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        if (i < 0) return;
        event.preventDefault();
        choose(current, opts[i].getAttribute('data-value'));
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeCurrent(true);
      } else if (event.key === 'Tab') {
        closeCurrent(false);
      }
    },
    true
  );

  /* Scrolling the line list moves the trigger away from the open list. */
  document.addEventListener(
    'scroll',
    function (event) {
      if (current && event.target !== current.panel && event.target.closest && event.target.closest('.mb-cart')) closeCurrent(false);
    },
    true
  );

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      if (current && !document.contains(current.button)) closeCurrent(false);
      enhance();
    });
  }

  enhance();
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
})();
/* ======================= end 2026-10-21 Frequency dropdown ======================= */

/* ======================= Cart-open state on <html> =======================
   moonbow-cart-drawer.css styles the shared right drawer only while it
   shows the cart. It reads two classes on <html> instead of `:has()` tests
   on `[style*="display: none"]`: inside `:has()`, a style-attribute test
   makes every inline style change on the page restyle the whole document.
     mb-cart-shown - the cart panel is not `display: none`
     mb-cart-open  - that, and the drawer itself is not `display: none`
   The same test as the old selectors, so the classes stay on through
   Alpine's 300ms leave transition exactly as the `:has()` rules did. Only
   the drawer's own subtree is observed. */
(function () {
  'use strict';

  var root = document.documentElement;

  function shown(el) {
    return !!el && (el.getAttribute('style') || '').indexOf('display: none') === -1;
  }

  function sync() {
    var modal = document.getElementById('modals-rightDrawer');
    var cart = modal && modal.querySelector('[data-cart-drawer]');
    var cartShown = shown(cart);
    root.classList.toggle('mb-cart-shown', cartShown);
    root.classList.toggle('mb-cart-open', cartShown && shown(modal));
  }

  function watch(modal) {
    sync();
    new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ['style'], childList: true, subtree: true });
  }

  /* Alpine teleports the drawer to <body> once its island loads. */
  var modal = document.getElementById('modals-rightDrawer');
  if (modal) {
    watch(modal);
  } else {
    var wait = new MutationObserver(function () {
      var found = document.getElementById('modals-rightDrawer');
      if (!found) return;
      wait.disconnect();
      watch(found);
    });
    wait.observe(document.body, { childList: true });
  }
})();
/* ======================= end Cart-open state ======================= */
